import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { communicationApi } from "./client";

export const communicationKeys = {
  hub: ["communication", "hub"] as const,
  meetings: (q: Record<string, unknown>) =>
    ["communication", "meetings", q] as const,
  meetingDetail: (id: string) => ["communication", "meeting", id] as const,
  minuteDetail: (id: string) => ["communication", "minute", id] as const,
  minutes: (search?: string) => ["communication", "minutes", search] as const,
  calendar: (q?: { fromDate?: string; toDate?: string }) =>
    ["communication", "calendar", q] as const,
  notifications: ["communication", "notifications"] as const,
};

export function useCommunicationHubQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: communicationKeys.hub,
    queryFn: () => communicationApi.hub(),
    enabled: options?.enabled ?? true,
  });
}

export function useCommunicationMeetingsQuery(query: {
  fromDate?: string;
  toDate?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: communicationKeys.meetings(query),
    queryFn: () => communicationApi.meetings(query),
  });
}

export function useCommunicationMeetingDetailQuery(id: string) {
  return useQuery({
    queryKey: communicationKeys.meetingDetail(id),
    queryFn: () => communicationApi.meetingById(id),
    enabled: !!id,
  });
}

export function useCommunicationMinutesQuery(search?: string) {
  return useQuery({
    queryKey: communicationKeys.minutes(search),
    queryFn: () => communicationApi.minutes(search),
  });
}

export function useCommunicationMinuteDetailQuery(id: string) {
  return useQuery({
    queryKey: communicationKeys.minuteDetail(id),
    queryFn: () => communicationApi.minuteById(id),
    enabled: !!id,
  });
}

export function useCommunicationCalendarQuery(query?: {
  fromDate?: string;
  toDate?: string;
}) {
  return useQuery({
    queryKey: communicationKeys.calendar(query),
    queryFn: () => communicationApi.calendar(query),
    // Keep previous result while changing date range to avoid UI remount/flicker.
    placeholderData: (previousData) => previousData,
  });
}

export function useCommunicationNotificationsQuery() {
  return useQuery({
    queryKey: communicationKeys.notifications,
    queryFn: () => communicationApi.notifications(),
  });
}

export function useMarkCommunicationNotificationReadMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => communicationApi.markNotificationRead(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["communication"] });
    },
  });
}
